/**
 * Translation Validator - Development Helper
 * Detects and warns about untranslated strings in the application
 * 
 * Usage:
 * 1. Add to your component: import { checkTranslations } from '../utils/translationValidator';
 * 2. In development, it will log warnings for missing translations
 * 3. Helps catch hardcoded strings during development
 */

import { useEffect } from 'react';

const MISSING_TRANSLATIONS = new Set();
const WARNED_KEYS = new Set();

/**
 * Custom t() wrapper that validates translations
 * Logs warnings for missing or fallback translations
 */
export const validateTranslation = (t, key, fallback = null) => {
  const isDev = process.env.NODE_ENV === 'development';
  
  try {
    const translated = t(key);
    
    // Check if translation is missing (returns the key itself)
    if (translated === key) {
      if (isDev && !WARNED_KEYS.has(key)) {
        console.warn(
          `🌐 [i18n] Missing translation: "${key}"`,
          fallback ? `\n   Fallback: "${fallback}"` : ''
        );
        WARNED_KEYS.add(key);
        MISSING_TRANSLATIONS.add(key);
      }
      return fallback || translated;
    }
    
    return translated;
  } catch (err) {
    if (isDev) {
      console.error(`🌐 [i18n] Error translating "${key}":`, err);
    }
    return fallback || key;
  }
};

/**
 * Checks if a string is likely hardcoded English
 * Returns true if it looks like untranslated English text
 */
export const isLikelyHardcoded = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Common English words that shouldn't appear in production
  const englishPatterns = [
    /^(Total|New|Pending|Approved|Rejected|Hidden|Active|Banned)\s/i,
    /\b(Users|Ads|Payments|Statistics|Overview|Dashboard)\b/i,
    /\d+\s+(day|days|hour|hours|minute|minutes)/i,
  ];
  
  return englishPatterns.some(pattern => pattern.test(text));
};

/**
 * Logs a summary of all missing translations
 * Call this in development mode to see what needs translation
 */
export const logMissingTranslations = () => {
  if (MISSING_TRANSLATIONS.size === 0) {
    console.log('✅ [i18n] All translations present!');
    return;
  }
  
  console.group(`⚠️ [i18n] ${MISSING_TRANSLATIONS.size} Missing Translations`);
  Array.from(MISSING_TRANSLATIONS).sort().forEach(key => {
    console.log(`  - ${key}`);
  });
  console.groupEnd();
};

/**
 * React Hook to validate translations on component mount
 * Usage: useTranslationCheck(t, ['key1', 'key2', 'key3']);
 */
export const useTranslationCheck = (t, keys = []) => {
  useEffect(() => {
    // Only run in development mode
    if (process.env.NODE_ENV !== 'development') return;
    
    const missing = keys.filter(key => {
      const translated = t(key);
      return translated === key; // Key returned as-is means missing
    });
    
    if (missing.length > 0) {
      console.warn(
        `🌐 [i18n] Component has ${missing.length} missing translations:`,
        missing
      );
    }
  }, [t, keys]);
};

/**
 * HOC to wrap components and check for hardcoded strings
 * Useful for development to catch English text in rendered output
 */
export const withTranslationCheck = (Component) => {
  if (process.env.NODE_ENV !== 'development') {
    return Component;
  }
  
  return function TranslationCheckedComponent(props) {
    const checkRenderedText = () => {
      // This is a simple check - in a real implementation you'd traverse the DOM
      const textNodes = document.querySelectorAll('*');
      textNodes.forEach(node => {
        const text = node.textContent?.trim();
        if (text && isLikelyHardcoded(text)) {
          console.warn(
            `🌐 [i18n] Possible hardcoded English text detected:`,
            text,
            node
          );
        }
      });
    };
    
    useEffect(() => {
      // Check after render
      const timer = setTimeout(checkRenderedText, 1000);
      return () => clearTimeout(timer);
    }, []);
    
    return <Component {...props} />;
  };
};

/**
 * Validates all admin translations are present
 * Call this in AdminModeration component
 */
export const validateAdminTranslations = (t) => {
  if (process.env.NODE_ENV !== 'development') return true;
  
  const requiredKeys = [
    'admin.dashboardOverview',
    'admin.userStats',
    'admin.listingStats',
    'admin.paymentStats',
    'admin.totalUsers',
    'admin.new7d',
    'admin.new30d',
    'admin.bannedUsers',
    'admin.adsApproved',
    'admin.adsPending',
    'admin.adsRejected',
    'admin.adsHidden',
    'admin.pendingPayments',
    'admin.approvedPayments',
    'admin.rejectedPayments',
    'admin.pending',
    'admin.approved',
    'admin.rejected',
  ];
  
  const missing = requiredKeys.filter(key => t(key) === key);
  
  if (missing.length > 0) {
    console.error(
      `❌ [i18n Admin] ${missing.length} required admin translations missing:`,
      missing
    );
    return false;
  }
  
  console.log('✅ [i18n Admin] All admin translations present');
  return true;
};

// Auto-log summary in development mode after a delay
if (process.env.NODE_ENV === 'development') {
  setTimeout(() => {
    logMissingTranslations();
  }, 5000); // Wait 5s after app load
}

export default {
  validateTranslation,
  isLikelyHardcoded,
  logMissingTranslations,
  useTranslationCheck,
  withTranslationCheck,
  validateAdminTranslations,
};
